import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

const GAP = 12;
// Matches arrowSize in common/components/Tooltip.
const ARROW_SIZE = 8;

/**
 * A tooltip for use inside an antd dropdown menu.
 *
 * The shared common/components/Tooltip cannot be used here. It renders its
 * popper as a sibling of the trigger, so inside a menu item it is clipped by
 * the `overflow: hidden` antd puts on `.ant-dropdown-menu` for its rounded
 * corners. Switching popper to `strategy: 'fixed'` does not help either,
 * because the dropdown is transformed, and a transformed ancestor becomes the
 * containing block for fixed positioning.
 *
 * Rendering into a portal sidesteps both problems: the tooltip is not a
 * descendant of the dropdown at all, so nothing can clip or displace it.
 *
 * Positioned to the left of the trigger because the additional-actions dropdown
 * sits at the right edge of the table.
 */
const MenuItemTooltip = ({ title, children }) => {
  const triggerRef = useRef(null);
  const [position, setPosition] = useState(null);

  const handleMouseEnter = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({
      top: rect.top + rect.height / 2,
      left: rect.left - GAP,
    });
  }, []);

  const handleMouseLeave = useCallback(() => setPosition(null), []);

  if (!title) {
    return children;
  }

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </span>
      {position &&
        createPortal(
          <div
            role="tooltip"
            // Same look as common/components/Tooltip: neutral-800 bubble,
            // centered small text, rounded, and it must not swallow pointer
            // events over the menu.
            className="fixed max-w-xs text-sm text-center rounded-lg bg-neutral-800 text-on-neutral-800 pointer-events-none"
            style={{
              top: position.top,
              left: position.left,
              transform: 'translate(-100%, -50%)',
              padding: '0.5em 1em',
              zIndex: 1100,
            }}
          >
            {title}
            <div
              style={{
                position: 'absolute',
                width: ARROW_SIZE,
                height: ARROW_SIZE,
                right: -ARROW_SIZE / 2,
                top: '50%',
                transform: 'translateY(-50%) rotate(45deg)',
                backgroundColor: 'var(--color-neutral-800)',
              }}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

MenuItemTooltip.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
};

MenuItemTooltip.defaultProps = {
  title: '',
  children: null,
};

export default MenuItemTooltip;
